/**
 * PublicHeader — sticky Top-Header für ALLE öffentlichen Seiten des
 * Backtest-Tools (Leaderboard, Pricing, Login, Register, Forgot/Reset-Password,
 * VerifyEmail, MagicLogin, MarketingHome, AcceptInvite).
 *
 * Inhalt:
 *   - Logo (links, klickt auf die Startseite)
 *   - Nav-Links: Leaderboard, Pricing, Anmelden
 *   - Primary-CTA: "Kostenlos starten"
 *   - LocaleSelector (Sprache + Land)
 *
 * Am Telefon bleibt es flach (Audit P9: zwei Zeilen mit 56-px-Logo machten
 * 174 px mitlaufenden Kopf — ein Fünftel der Bildschirmhöhe, dauerhaft):
 *   - Logo 34 px hoch (28 px unter 480 px), Sprachwahl nur mit Kürzel,
 *     kompakte Knöpfe. „Anmelden" bleibt sichtbar: wer ein Konto hat, findet
 *     den Weg im Kopf, nicht erst über einen gesperrten Reiter.
 *   - Der Werbeknopf trägt unter 480 px den kurzen Text („Registrieren"
 *     statt „Kostenlos starten"), sonst passte „Anmelden" nicht daneben.
 *     Welcher der beiden erscheint, entscheidet CSS — keine Weiche auf
 *     `innerWidth`, die beim Drehen des Geräts stehen bliebe.
 *
 * Wenn der User eingeloggt ist (z.B. ein Advisor öffnet /leaderboard):
 *   - Anmelden + Kostenlos-CTA werden ausgeblendet (User braucht keinen Login mehr)
 *   - Statt Anmelden steht "Zur App →" → /tables
 */
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';
import { useTranslation } from '../hooks/useTranslation';
import { useAnmeldeFreigabe } from '../hooks/useAnmeldeFreigabe';
import LocaleSelector from './LocaleSelector';
import { DarstellungKnopf } from '../shared/Darstellung';
import { useDarstellungAnfang } from './DarstellungAnfang';
import { DARSTELLUNG_COOKIE } from '../utils/darstellungCookie';
import BannerStrip from './BannerStrip';
import Logo from './Logo';

/**
 * Die Hinweis-Leiste (`BannerStrip`, Beta/Wartung) steht auf JEDER öffentlichen
 * Seite ganz oben, über dem Kopf (Entscheidung 11.10.2026 — die Preisseite und
 * die Produktseiten hatten sie seit 30.09.2026 über dem Footer).
 */
export default function PublicHeader() {
  // Der Werbeknopf erscheint nur, wo Land UND Plattform die Registrierung freigeben.
  const { registrierung: registrierungOffen } = useAnmeldeFreigabe();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguage();
  const darstellungAnfang = useDarstellungAnfang();

  return (
    <>
      {/* Admin-konfigurierbare Beta-/Maintenance-Leiste für alle öffentlichen Seiten */}
      <BannerStrip scope="public" />
      <header className="ph-root" style={{
        background: 'var(--bg-primary)',
        position: 'sticky', top: 0, zIndex: 50,
      }}>
        <div className="ph-inner" style={{
          maxWidth: 1200, margin: '0 auto',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px', gap: 12,
        }}>
          {/* Auf die Startseite DIESER Sprachfassung, nicht auf `/`: Die
              Wurzel leitet je nach Sprache des Besuchers weiter — ein
              Crawler folgte von jeder Seite einer Weiterleitung. */}
          <Link to={getLocalizedPath('/')} className="ph-logo-link" style={{
            display: 'inline-flex', alignItems: 'center', gap: 10,
            textDecoration: 'none', flexShrink: 0,
          }}>
            <Logo
              alt="Paperball"
              className="ph-logo-img"
              style={{ height: 56, width: 'auto', display: 'block', maxWidth: '100%' }}
            />
          </Link>

          <nav className="ph-nav" style={{
            display: 'flex', gap: 18, alignItems: 'center', fontSize: '1rem',
          }}>
            {/* Leaderboard/Pricing sind in der öffentlichen Tab-Leiste
                (PublicTabNav) — hier im Header bewusst nicht mehr dupliziert. */}

            {/* Hell/Dunkel — im Kopf, also auch ohne Anmeldung erreichbar */}
            <DarstellungKnopf cookie={DARSTELLUNG_COOKIE} anfang={darstellungAnfang} beschriftung={t('darstellung.dunkel', 'Dunkle Darstellung')} groesse={36} className="ph-darstellung" />

            {/* Locale-Selector immer sichtbar */}
            <div className="ph-locale">
              <LocaleSelector />
            </div>

            {user ? (
              // Eingeloggt: schickt zurück in die App
              <Link
                to="/tables"
                className="ph-cta"
                style={{ boxShadow: 'var(--knopf-schatten)',
                  padding: '8px 16px', borderRadius: 'var(--radius-pill)',
                  background: 'var(--knopf-verlauf)', color: 'var(--knopf-text)',
                  textDecoration: 'none', fontWeight: 600, fontSize: '1rem',
                  whiteSpace: 'nowrap',
                }}
              >
                {t('public_nav.to_app', 'Zur App →')}
              </Link>
            ) : (
              <>
                <Link
                  to={getLocalizedPath('/login')}
                  className="ph-link"
                  style={{ color: 'var(--text-marke)', textDecoration: 'none', fontWeight: 500 }}
                >
                  {t('public_nav.signin', 'Anmelden')}
                </Link>
                {registrierungOffen && (
                <Link
                  to={getLocalizedPath('/register')}
                  className="ph-cta"
                  style={{ boxShadow: 'var(--knopf-schatten)',
                    padding: '8px 16px', borderRadius: 'var(--radius-pill)',
                    background: 'var(--knopf-verlauf)', color: 'var(--knopf-text)',
                    textDecoration: 'none', fontWeight: 600, fontSize: '1rem',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span className="ph-cta-lang">{t('public_nav.signup', 'Kostenlos starten')}</span>
                  <span className="ph-cta-kurz">{t('public_nav.signup_short', 'Registrieren')}</span>
                </Link>
                )}
              </>
            )}
          </nav>
        </div>
      </header>

      <style>{`
        .ph-cta-kurz { display: none; }
        /* Telefon (≤720px): eine Zeile, kleines Logo, kompakte Sprachwahl */
        @media (max-width: 720px) {
          .ph-inner {
            padding: 8px 12px !important;
            gap: 8px !important;
          }
          .ph-logo-img { height: 34px !important; }
          .ph-nav {
            gap: 8px !important;
            font-size: 1rem !important;
            min-width: 0;
          }
          .ph-locale .locale-lang-name, .ph-locale .locale-sep { display: none; }
          .ph-cta { padding: 7px 12px !important; font-size: 1rem !important; }
        }
        /* Sehr schmal (≤480px): kleineres Logo, kompakte Knöpfe, EINE Zeile.
           Gemessen am 30.09.2026 bei 375 px: In es („Iniciar sesión" +
           „Registrarse") und pl („Zaloguj się" + „Zarejestruj się") brach
           der Kopf in zwei Zeilen um — 91 statt 50 px mitlaufender Kopf.
           Die Sprachwahl zeigt hier nur das Länderkürzel ohne Globus (der
           Knopf bleibt als Sprachwahl erkennbar, die Wörter bleiben ganz);
           damit passen alle elf Sprachen mit Luft. Der Umbruch bleibt als
           Netz für Schriften, die breiter laufen als gemessen: lieber eine
           zweite Zeile als ein Knopf, der aus dem Bild läuft. */
        @media (max-width: 480px) {
          /* 24 px Logo und 4 px Abstand: Platz für den Hell/Dunkel-Knopf (07.10.2026) */
          .ph-logo-img { height: 24px !important; }
          .ph-inner { gap: 6px !important; }
          .ph-nav {
            flex-wrap: wrap;
            justify-content: flex-end;
            row-gap: 6px;
            gap: 4px !important;
          }
          .ph-locale .locale-globe { display: none; }
          .ph-locale .locale-btn { padding: 5px 7px !important; }
          .ph-link { font-size: 1rem !important; }
          .ph-cta { padding: 6px 10px !important; font-size: 1rem !important; }
          .ph-cta-lang { display: none; }
          .ph-cta-kurz { display: inline; }
          /* Hell/Dunkel-Knopf: 32 px statt 36 — sonst bricht die Zeile auf Deutsch um */
          .ph-darstellung { width: 32px !important; height: 32px !important; }
        }
      `}</style>
    </>
  );
}
